/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Avatar } from '@components/atoms'
import { FullModalLayout, ImageUplodader } from '@components/molecules'
import { mono, boShared } from '@components/systems/backoffice/backoffice.tokens'

import { useAppDispatch, useAppSelector, userActions } from '@redux/index'
import { UserType } from '@schemas'
import { UserStateType } from '@redux/types'

/**
 * 관리자 My Page — 백오피스 계정(USERS) 본인 정보.
 *
 * 시즌 상품 상세(`/main/season/:seasonId/product/:id`)와 같은 방식이다: 모달 컴포넌트가 아니라
 * 자기 라우트를 가진 전체 화면(FullModalLayout). 그래야 뒤로가기로 닫히고 새로고침에도 산다.
 *
 * 여기서 부르는 API 는 전부 `/auth/*` 의 self 엔드포인트다 — 경로에 사용자 ID 를 넘기지 않으므로
 * 남의 계정을 건드릴 수 없다. 관리자 그리드의 `/users/:id/...` 와는 다른 경로다.
 * 이메일은 로그인 ID 이고 USERS 에는 인증 흐름이 없어 읽기 전용이다.
 *
 * 화면 문법은 홈 My Info 와 같다 — 스펙 행(아바타가 첫 행) · 밑줄 입력 · 블랙 CTA 는 화면당 하나(프로필 저장),
 * 비밀번호 변경·이미지 변경은 고스트.
 */
const AdminMyPage = () => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const user: UserType = useAppSelector(state => state.user.user)
	const selfStatus: UserStateType['selfStatus'] = useAppSelector(
		state => state.user.selfStatus,
	)
	const errorMessage: string = useAppSelector(state => state.user.selfErrorMessage)
	const isUploading: boolean = useAppSelector(state => state.user.isUploadingProfileImage)

	// state
	const [name, setName] = React.useState<string>('')
	const [showImagePicker, setShowImagePicker] = React.useState<boolean>(false)
	const [prevPassword, setPrevPassword] = React.useState<string>('')
	const [newPassword, setNewPassword] = React.useState<string>('')
	const [confirmPassword, setConfirmPassword] = React.useState<string>('')
	const [localError, setLocalError] = React.useState<string>('')

	React.useEffect(() => {
		setName(user.name)
	}, [user.name])

	React.useEffect(() => {
		// 화면에 들어올 때마다 이전 결과를 지운다 — 지난번 성공 문구가 남아 있으면 안 된다
		reduxDispatch(userActions.SELF_STATUS('idle'))
		reduxDispatch(userActions.SELF_ERROR_MESSAGE(''))
	}, [])

	const isBusy: boolean = selfStatus === 'saving' || isUploading

	const onProfileSave = () => {
		setLocalError('')

		if (name.trim() === '') {
			return setLocalError('이름을 입력해 주세요.')
		}
		if (name.trim().length > 30) {
			return setLocalError('이름은 30자까지 입력할 수 있습니다.')
		}

		reduxDispatch(
			userActions.UPDATE_SELF_PROFILE({ name: name.trim(), language: user.language }),
		)
	}

	const onPasswordSave = () => {
		setLocalError('')

		if (prevPassword === '' || newPassword === '') {
			return setLocalError('현재 비밀번호와 새 비밀번호를 모두 입력해 주세요.')
		}
		if (newPassword.length < 8 || newPassword.length > 15) {
			return setLocalError('새 비밀번호는 8~15자로 입력해 주세요.')
		}
		if (newPassword !== confirmPassword) {
			return setLocalError('새 비밀번호가 서로 다릅니다.')
		}

		reduxDispatch(
			userActions.UPDATE_SELF_PASSWORD({
				prevPassword: prevPassword,
				newPassword: newPassword,
			}),
		)
		setPrevPassword('')
		setNewPassword('')
		setConfirmPassword('')
	}

	const onGetImage = (aFile: File) => {
		setShowImagePicker(false)
		setLocalError('')
		reduxDispatch(userActions.UPDATE_SELF_PROFILE_IMAGE({ file: aFile }))
	}

	const message: string = localError !== '' ? localError : errorMessage

	return (
		<View css={style}>
			<FullModalLayout title="My Page" size="small">
				<View className="myPageOuterView">
					{/* 프로필 */}
					<View className="block">
						<span className="boKicker">프로필</span>

						<View className="spec">
							<View className="boSpecRow avatarRow">
								<span className="boSpecLabel">이미지</span>
								<View className="boSpecValue avatarValue">
									{showImagePicker ? (
										<View className="pickerView">
											<ImageUplodader onGetImage={onGetImage} />
											<button
												type="button"
												className="boBtnText"
												onClick={() => setShowImagePicker(false)}
											>
												취소
											</button>
										</View>
									) : (
										<View className="avatarSide">
											<Avatar
												className="myPageAvatar"
												picture={user.profileImageUrl}
												name={user.name}
												color={
													user.profileColor === '' ? mono.lineSoft : user.profileColor
												}
												size={64}
												extpandingDisable={true}
											/>
											<View className="avatarActions">
												<button
													type="button"
													className="boBtnGhost"
													disabled={isBusy}
													onClick={() => setShowImagePicker(true)}
												>
													{user.profileImageUrl === '' ? '이미지 등록' : '이미지 변경'}
												</button>
												<p className="hint">
													Studio 와 백오피스에서 이름 옆에 보입니다. 정사각형 이미지가
													가장 잘 맞습니다. JPEG · PNG · WebP · GIF, 8MB 까지.
												</p>
											</View>
										</View>
									)}
								</View>
							</View>

							<View className="boSpecRow">
								<label className="boSpecLabel" htmlFor="myPageName">
									이름
								</label>
								<input
									id="myPageName"
									className="boInput"
									type="text"
									value={name}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
										setName(e.target.value)
									}
								/>
							</View>

							<View className="boSpecRow">
								<span className="boSpecLabel">이메일</span>
								<View className="boSpecValue">
									<span className="readonly">{user.email}</span>
									<p className="hint">
										로그인 ID 라 여기서 바꾸지 않습니다. 변경이 필요하면 관리자에게
										요청하세요.
									</p>
								</View>
							</View>
						</View>

						<View className="actionRow">
							<button
								type="button"
								className="boBtn"
								disabled={isBusy}
								onClick={onProfileSave}
							>
								저장
							</button>
						</View>
					</View>

					{/* 비밀번호 */}
					<View className="block">
						<span className="boKicker">비밀번호 변경</span>
						<p className="boLead">바꿔도 로그인 상태는 유지됩니다.</p>

						<View className="spec">
							<View className="boSpecRow">
								<label className="boSpecLabel" htmlFor="myPagePrevPassword">
									현재 비밀번호
								</label>
								<input
									id="myPagePrevPassword"
									className="boInput"
									type="password"
									autoComplete="current-password"
									value={prevPassword}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
										setPrevPassword(e.target.value)
									}
								/>
							</View>
							<View className="boSpecRow">
								<label className="boSpecLabel" htmlFor="myPageNewPassword">
									새 비밀번호 (8~15자)
								</label>
								<input
									id="myPageNewPassword"
									className="boInput"
									type="password"
									autoComplete="new-password"
									value={newPassword}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
										setNewPassword(e.target.value)
									}
								/>
							</View>
							<View className="boSpecRow">
								<label className="boSpecLabel" htmlFor="myPageConfirmPassword">
									새 비밀번호 확인
								</label>
								<input
									id="myPageConfirmPassword"
									className="boInput"
									type="password"
									autoComplete="new-password"
									value={confirmPassword}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
										setConfirmPassword(e.target.value)
									}
								/>
							</View>
						</View>

						<View className="actionRow">
							<button
								type="button"
								className="boBtnGhost"
								disabled={isBusy}
								onClick={onPasswordSave}
							>
								비밀번호 변경
							</button>
						</View>
					</View>

					{message !== '' && <p className="boError message">{message}</p>}
					{message === '' && selfStatus === 'saved' && (
						<p className="successMessage">저장했습니다.</p>
					)}
				</View>
			</FullModalLayout>
		</View>
	)
}

export default AdminMyPage

const style = css({
	...boShared,
	position: 'relative',
	width: '100%',

	'.myPageOuterView': {
		width: '100%',
		textAlign: 'left',

		'.block': {
			paddingBottom: 32,
			marginBottom: 32,
			borderBottom: `1px solid ${mono.line}`,

			'.boLead': { marginTop: 8 },

			'.spec': { marginTop: 16, borderTop: `1px solid ${mono.line}` },

			'.boSpecRow': { alignItems: 'flex-start', padding: '14px 0' },
			'.boSpecLabel': { paddingTop: 12 },
			'.avatarRow .boSpecLabel': { paddingTop: 4 },
			'.boInput': { height: 40 },

			'.readonly': { display: 'block', paddingTop: 10, fontSize: 14, fontWeight: 300 },

			'.hint': {
				marginTop: 6,
				fontSize: 12,
				fontWeight: 300,
				lineHeight: 1.6,
				color: mono.faint,
				maxWidth: 420,
			},

			'.avatarValue': { width: '100%' },
			'.avatarSide': { display: 'flex', alignItems: 'flex-start', gap: 20 },
			'.avatarActions': {
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'flex-start',
				gap: 4,
				paddingTop: 4,
			},
			'.pickerView': {
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'flex-start',
				gap: 8,
				width: '100%',
			},

			'.actionRow': {
				display: 'flex',
				justifyContent: 'flex-end',
				marginTop: 20,
			},
		},

		'.message': { display: 'block', fontSize: 13 },
		'.successMessage': { margin: 0, fontSize: 13, fontWeight: 300, color: mono.dim },
	},
})
